import React from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import ntxLogo from '../../assets/ntx-logo.png';

export const SiteFooter = ({ scrollToSection }) => (
  <footer className="bg-black py-12 border-t border-zinc-800">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
        <div>
          <Link to="/">
            <img src={ntxLogo} alt="North Texas Welding and Fabrication Logo" className="h-16 w-auto mb-3" data-testid="footer-logo" />
          </Link>
          <p className="text-zinc-400 text-sm font-semibold mb-1">Built for the Texas Landowner</p>
          <p className="text-zinc-500 text-xs">Fence &bull; Land &bull; Buildings &bull; Concrete &bull; Gates</p>
        </div>

        <div>
          <h4 className="text-white font-bold mb-4 text-sm">Services</h4>
          <ul className="space-y-2">
            {[
              { label: 'Pipe Fencing', href: '/pipe-fencing' },
              { label: 'Land Services', href: '/land-services' },
              { label: 'Metal Buildings', href: '/metal-buildings' },
              { label: 'Concrete', href: '/concrete' },
              { label: 'Custom Gates', href: '/custom-gates' },
              { label: 'Gravel & Dirt Work', href: '/gravel-dirt-work' },
            ].map(link => (
              <li key={link.href}>
                <Link to={link.href} className="text-zinc-400 hover:text-red-500 transition-colors text-sm">{link.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold mb-4 text-sm">Service Areas</h4>
          <ul className="space-y-2">
            {[
              { label: 'Springtown, TX', href: '/pipe-fence-springtown-tx' },
              { label: 'Weatherford, TX', href: '/pipe-fence-weatherford-tx' },
              { label: 'Azle, TX', href: '/pipe-fence-azle-tx' },
            ].map(link => (
              <li key={link.href}>
                <Link to={link.href} className="text-zinc-400 hover:text-red-500 transition-colors text-sm">{link.label}</Link>
              </li>
            ))}
            <li className="text-zinc-500 text-xs pt-1">+ Decatur, Bridgeport, Fort Worth & more</li>
          </ul>
          <h4 className="text-white font-bold mb-3 mt-6 text-sm">Quick Links</h4>
          <ul className="space-y-2">
            <li><Link to="/instant-estimate" className="text-zinc-400 hover:text-red-500 transition-colors text-sm">Instant Estimate</Link></li>
            <li><Link to="/financing" className="text-zinc-400 hover:text-red-500 transition-colors text-sm">Financing</Link></li>
            <li><Link to="/blog" className="text-zinc-400 hover:text-red-500 transition-colors text-sm">Blog</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold mb-4 text-sm">Contact Info</h4>
          <ul className="space-y-3 text-sm">
            <li><a href="tel:8177235006" className="text-zinc-400 hover:text-red-500 transition-colors">817-723-5006</a></li>
            <li><a href="mailto:Northtexasweldingandfab@gmail.com" className="text-zinc-400 hover:text-red-500 transition-colors break-all text-xs">Northtexasweldingandfab@gmail.com</a></li>
            <li className="text-zinc-500">Springtown, TX & Surrounding Areas</li>
            <li className="flex items-center gap-3 pt-2">
              <a href="https://www.facebook.com/share/1bUooz3NPo/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-blue-600 transition-colors group" aria-label="Facebook">
                <svg className="w-4 h-4 text-zinc-400 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://share.google/TfFCVxA4nlmSDcgrZ" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-red-600 transition-colors group" aria-label="Google">
                <svg className="w-4 h-4 text-zinc-400 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              </a>
              <a href="https://m.yelp.com/biz/north-texas-welding-and-fabrication-springtown-2" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-red-700 transition-colors group" aria-label="Yelp">
                <svg className="w-4 h-4 text-zinc-400 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.16 12.594l-4.995 1.433c-.96.276-1.74-.8-1.176-1.63l2.996-4.428c.564-.83 1.836-.437 1.836.57v3.27c0 .33-.24.63-.56.72l-.1.065zm-8.836 5.478l-1.212-5.076c-.228-.96 1.015-1.572 1.744-.86l3.865 3.78c.728.712.12 1.884-.88 1.692l-2.856-.536c-.324-.06-.576-.3-.66-.62v-.38zM6.4 12.69l4.86-1.83c.924-.348.576-1.68-.396-1.668l-5.172.06c-.972.012-1.296 1.26-.468 1.8l.612.396c.276.18.432.48.42.804l.144.438zM11.74 3.348l1.536 4.968c.288.936-1.008 1.488-1.68.72l-3.564-4.08c-.672-.768.012-1.908.984-1.644l2.088.564c.312.084.552.312.636.624v-.152zm-.54 14.952l-1.26-5.016c-.24-.948 1.044-1.524 1.74-.78l3.696 3.936c.696.744-.024 1.896-1.032 1.596l-2.52-.84c-.312-.104-.54-.348-.612-.672l-.012-.224z"/></svg>
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-zinc-800 pt-8 flex items-center justify-between">
        <p className="text-zinc-500 text-xs">
          &copy; {new Date().getFullYear()} North Texas Welding and Fabrication, LLC. All rights reserved.
        </p>
        <div className="flex items-center gap-3">
          <a href="/sales/login" className="text-zinc-700 hover:text-zinc-500 transition-colors text-xs" data-testid="sales-portal-link" aria-label="Sales Portal">
            Sales Portal
          </a>
          <a href="/admin/login" className="text-zinc-700 hover:text-zinc-500 transition-colors" data-testid="admin-login-link" aria-label="Admin">
            <Lock size={14} />
          </a>
        </div>
      </div>
    </div>
  </footer>
);
